import { createNetworkRequestHook } from '@/utils/createNetworkRequestHook'
import { createPaginatedNetworkRequestHook } from '@/utils/createPaginatedNetworkRequestHook'

import {
  AddAssetPTUError,
  AddAssetPTUPayload,
  AddAssetPTUResponse,
  AddAssetVehiclePayload,
  AddAssetVehicleResponse,
  Asset,
  AssetsPayload,
  BridgedFleetPayload,
  DeleteAssetPayload,
  DeleteAssetResponse,
  MyVehiclesPayload,
  PatchMyPtuPayload,
  PatchMyPtuResponse,
  PatchMyVehiclePayload,
  PatchMyVehicleResponse,
  PTUPayload,
  SinglePtuResponse,
  SingleVehicleResponse,
  TrackingSourceResponse,
} from '@/lib/features/Assets/interfaces'

import { useNetworkMutation } from '@/lib/utils/request'

const baseUrl = 'v1/assets'

export const useAllAssets = createPaginatedNetworkRequestHook<Asset, AssetsPayload>(
  `${baseUrl}/assets/`,
)
export const useSingleAsset = createNetworkRequestHook<Asset, { id?: number }>(
  'v1/assets/assets/${params.id}/',
)
export const useMyVehicles = createPaginatedNetworkRequestHook<Asset, MyVehiclesPayload>(
  `${baseUrl}/vehicles/my/`,
)
export const useBridgedAssets = createPaginatedNetworkRequestHook<Asset, BridgedFleetPayload>(
  `${baseUrl}/vehicles/`,
)
export const usePtuAssets = createPaginatedNetworkRequestHook<Asset, PTUPayload>(
  `${baseUrl}/portable-tracking-units/`,
)
export const useTrackingSources = createNetworkRequestHook<TrackingSourceResponse>(
  `${baseUrl}/telemetries/`,
)
export const useSingleVehicle = createNetworkRequestHook<SingleVehicleResponse, { id: number }>(
  'v1/assets/vehicles/${params.id}/',
)
export const useSinglePtu = createNetworkRequestHook<SinglePtuResponse, { id: number }>(
  'v1/assets/portable-tracking-units/${params.id}/',
)

export const useAddVehicle = () =>
  useNetworkMutation<AddAssetVehicleResponse, AddAssetVehiclePayload>({
    url: `${baseUrl}/vehicles/my/`,
    method: 'post',
  })

export const useAddPTU = () =>
  useNetworkMutation<AddAssetPTUResponse, AddAssetPTUPayload, AddAssetPTUError>({
    url: `${baseUrl}/portable-tracking-units/my/`,
    method: 'post',
  })

export const usePatchVehicle = () =>
  useNetworkMutation<
    PatchMyVehicleResponse,
    {
      data: Omit<PatchMyVehiclePayload, 'objectId'>
      params: { objectId: PatchMyVehiclePayload['objectId'] }
    }
  >({
    url: `${baseUrl}/vehicles/my/\${params.objectId}/`,
    method: 'patch',
  })

export const usePatchPTU = () =>
  useNetworkMutation<
    PatchMyPtuResponse,
    {
      data: Omit<PatchMyPtuPayload, 'objectId'>
      params: { objectId: PatchMyPtuPayload['objectId'] }
    }
  >({
    url: `${baseUrl}/portable-tracking-units/my/\${params.objectId}/`,
    method: 'patch',
  })

export const useDeleteVehicle = () =>
  useNetworkMutation<DeleteAssetResponse, { params: { id: DeleteAssetPayload } }>({
    url: `${baseUrl}/vehicles/my/\${params.id}/`,
    method: 'delete',
  })

export const useDeletePTU = () =>
  useNetworkMutation<DeleteAssetResponse, { params: { id: DeleteAssetPayload } }>({
    url: `${baseUrl}/portable-tracking-units/my/\${params.id}/`,
    method: 'delete',
  })
